import { Component, EventEmitter, Input, OnChanges, Output } from "@angular/core";
import { AbstractControl, FormArray, FormBuilder, FormGroup } from "@angular/forms";
import { DomSanitizer, SafeHtml } from "@angular/platform-browser";
import { faAngleDown, faAngleUp, IconDefinition } from "@fortawesome/free-solid-svg-icons";
import { IaqoboAirPermission } from "app_locobook/shared/iaqobo-air-permission.enum";
import { defaultRatingConfiguration } from "libraries/component/rating-table/default-rating-configuration";
import { IRatingConfiguration } from "libraries/component/rating-table/rating-table.component";
import { DateMomentPipe } from "libraries/date/dateMoment.pipe";
import { ILabels } from "libraries/interfaces/ILabels";
import { wenji } from "libraries/interfaces/IWenji";
import { Yii } from "libraries/interfaces/Yii";
import { IFieldSpecs, ISliderFieldSpecs } from "libraries/module/formbuilder/fields/interfaces/field-specs";
import { ICheckboxOption } from "libraries/module/formbuilder/fields/multi-checkbox-field/ICheckboxOption";
import { LocalizedPipe } from "libraries/pipes/localized/localized.pipe";
import { LabelService } from "libraries/service/label/label.service";
import { IAirQuoteFilter } from "libraries/service/resources/airTrip/IAirQuoteFilter";
import { IGetAirTripQuotesDictionary } from "libraries/service/resources/airTrip/IGetAirTripQuotes";
import { ConfigurationResourceService } from "libraries/service/resources/configuration/configurationResource.service";
import { ICodeName } from "libraries/service/resources/model/shared/ICodeName";
import { PermissionResourceService } from "libraries/service/resources/permission/permissionResource.service";
import _pull from 'lodash-es/pull';
import _uniq from 'lodash-es/uniq';
import { forkJoin, Observable, Subscription } from "rxjs";
import { FILTER_CONFIG, IFilterConfig } from "../../config/filter.config";
import { FilterService } from "../../services/filter/filter.service";
import { FILTER_COMPONENT } from "./config/wording";



export interface IFilterFormValue {
    [key: string]: string[][];
}

@Component({
    selector: 'quote-filter',
    templateUrl: 'quote-filter.component.html',
    styleUrls: ['./quote-filter.component.scss']
})
export class QuoteFilterComponent implements OnChanges {

    @Input()
    public filterData: IAirQuoteFilter;

    @Input()
    public dictionary: IGetAirTripQuotesDictionary;

    @Output()
    public readonly onChangeFilter: EventEmitter<IFilterFormValue> = new EventEmitter();

    public filterConfig: IFilterConfig;
    public filterFormGroup: FormGroup;
    public fieldsOptions: { [controlName: string]: ICheckboxOption[] | string[][] };


    public get filterDataKeys(): string[] {
        return this.filterDataKeysModel;
    }

    public wording: ILabels = FILTER_COMPONENT;

    public iconAngleDown: IconDefinition = faAngleDown;

    public iconAngleUp: IconDefinition = faAngleUp;

    protected filterDataKeysModel: string[];
    private valueChangesSubscription: Subscription;
    private readonly dateFormat: { [key: string]: string } = JSON.parse(wenji.ui.dateFormat);

    constructor(
        protected readonly formBuilder: FormBuilder,
        protected readonly filterService: FilterService,
        protected readonly labelService: LabelService,
        protected readonly dateMomentPipe: DateMomentPipe,
        protected readonly permissionResourceService: PermissionResourceService,
        protected readonly configurationResourceService: ConfigurationResourceService,
        protected readonly localizedPipe: LocalizedPipe,
        protected readonly domSanitizer: DomSanitizer,
    ) {
        this.filterConfig = {...FILTER_CONFIG};
    }

    public initFilterConfig(): Observable<(Observable<boolean> | IRatingConfiguration)[]> {
        const fareTypePermission: Observable<boolean> = this.permissionResourceService.hasPermission(IaqoboAirPermission.IQBA_FARETYPE_SHOW);
        const hostPermission: Observable<boolean> = this.permissionResourceService.hasPermission(IaqoboAirPermission.IQBA_HOSTCODE_SHOW);
        const costPermission: Observable<boolean> = this.permissionResourceService.hasPermission(IaqoboAirPermission.IQBA_BOOKING_PRICE_COST_SHOW);
        const retailPermission: Observable<boolean> = this.permissionResourceService.hasPermission(IaqoboAirPermission.IQBA_BOOKING_PRICE_RETAIL_SHOW);
        const carbonFootprintPermission: Observable<boolean> = this.permissionResourceService.hasPermission(IaqoboAirPermission.SHARED_AIR_CARBON_FOOTPRINT_SHOW);
        const ratingPermission: Observable<boolean> = this.permissionResourceService.hasPermission(IaqoboAirPermission.IQBA_QUOTE_RATING_YUCANGOO_VIEW);

        return forkJoin([
            fareTypePermission,
            hostPermission,
            costPermission,
            retailPermission,
            carbonFootprintPermission,
            ratingPermission,
            this.configurationResourceService.get(["wui", "shared_air", "config", "rating", "rating.json"], defaultRatingConfiguration) as unknown as IRatingConfiguration
        ]);
    }

    public ngOnChanges(): void {
        this.initQuoteFilters();
    }

    protected initQuoteFilters(): void{
        this.filterFormGroup = this.filterFormGroup || this.formBuilder.group({});

        if (this.valueChangesSubscription) {
            this.valueChangesSubscription.unsubscribe();
        }

        this.initFilterFormGroup();
        this.initFieldsOptions();

        this.valueChangesSubscription = this.filterFormGroup.valueChanges
            .subscribe({
                next: (newValue: IFilterFormValue): void => {
                    this.onChangeFilter.emit(newValue);
                }
            });

        this.initFilterConfig().subscribe({
            next: ([fareTypePerm, hostPerm, costPerm, retailPerm, carbonFootprintPerm, ratingPerm, ratingConfig]: Array<(Observable<boolean> | IRatingConfiguration)>): void => {
                this.filterConfig.fareType = fareTypePerm ? this.filterConfig.fareType : null;
                this.filterConfig.host = hostPerm ? this.filterConfig.host : null;
                this.filterConfig.cost = costPerm ? this.filterConfig.cost : null;
                this.filterConfig.retail = retailPerm ? this.filterConfig.retail : null;
                this.filterConfig.rating = ratingPerm ? this.filterConfig.rating : null;
                this.filterConfig.carbonFootprint = carbonFootprintPerm ? this.filterConfig.carbonFootprint : null;

                if (this.filterConfig && this.filterConfig.rating && ratingConfig && (ratingConfig as unknown as IRatingConfiguration).wording) {
                    this.filterConfig.rating.filterLabel = this.localizedPipe.transform((ratingConfig as unknown as IRatingConfiguration).wording);
                }

                this.filterDataKeysModel = Object.keys(this.filterData || {})
                    .filter((controlName: string): boolean => Boolean(this.filterConfig[controlName]))
                    .filter((controlName: string): boolean => {
                        const keys: string[] = this.filterConfig[controlName].isBySegment
                            ? Object.values(this.filterData[controlName] || {})
                                .flatMap((value: object) => Object.keys(value))
                            : Object.keys(this.filterData[controlName]);


                        return keys.length > 0;
                    });

                this.initFilterConfigCallback();
            }
        });
    }


    public toggleFilterfield(controlName: string): void {
        this.filterConfig[controlName].show = !this.filterConfig[controlName].show;
    }

    public getFormControlKeys(controlName: string): string[] {
        const keys: string[] = Object.keys(this.filterFormGroup.get(controlName).value);
        return keys;
    }

    public getSliderFieldConfig(formGroup: AbstractControl, formControlName: string): ISliderFieldSpecs {
        return {
            formControlName,
            formGroup: formGroup as FormGroup,
            min: 0,
            max: 100,
            range: true,
        };
    }

    public getCheckboxFieldConfig(formGroup: AbstractControl, formControlName: string): IFieldSpecs {
        return {
            formControlName,
            formGroup: formGroup as FormGroup,
        };
    }

    public getSegmentLabel(key: string): string {
        return +key === 0 ? `${Yii.t('wui-iqba-js', 'Aller')} :` : `${Yii.t('wui-iqba-js', 'Retour')} :`;
    }

    public getSegmentLabelMultiple(key: string): string {
        return `${Yii.t('wui-iqba-js', 'Vol')} ${+key+1}`;
    }


    public getRangeLabel(controlName: string, key: string): SafeHtml {

        if (controlName === 'cost' || controlName === 'retail') {
            //Assume all prices have the same currency
            const currency: { code: string, decimals: number } = Object.values(this.filterData[controlName])[0].currency;
            return this.filterService.getRangeLabels(
                this.fieldsOptions[controlName][key],
                controlName,
                this.filterFormGroup.get(controlName).get(key).value,
                currency
            );
        }
        return this.domSanitizer.bypassSecurityTrustHtml(
            this.filterService.getRangeLabels(
                this.fieldsOptions[controlName][key],
                controlName,
                this.filterFormGroup.get(controlName).get(key).value
            )
        );
    }

    /**
     * @description : this method is necessary in classes that extends QuoteFilterComponent to target the subscribe=>next execution time
     */
    protected initFilterConfigCallback(): void{

    }

    protected initFilterFormControl(controlName: string): FormArray {
        const formArray: FormArray = new FormArray([]);
        const parsed: Map<number, string[]> = this.filterService.getReducedFilterKeys(this.filterData, controlName);

        parsed
            .forEach((segmentItem: string[]) => {
                formArray.push(this.formBuilder.control(this.getDefaultValue(controlName, segmentItem)));
            });
        return formArray;
    }

    protected getDefaultValue(controlName: string, data: string[]): string[] | number[] {
        const parsedFilterData: string[] = _uniq(data);
        return FILTER_CONFIG[controlName].type === 'checkbox' ?
            parsedFilterData :
            [0, 100];
    }

    private initFilterFormGroup(): void {
        Object.keys(this.filterFormGroup.controls)
            .forEach((controlName: string) => {
                this.filterFormGroup.removeControl(controlName);
            });

        const controlNames: string[] = Object.keys(this.filterData || {});

        controlNames.forEach(
            (controlName: string): void => {
                this.filterFormGroup.addControl(controlName, this.initFilterFormControl(controlName));
            }
        );
    }

    private initFieldsOptions(): void {
        this.fieldsOptions = Object.keys(this.filterData || {})
            .reduce(
                (acc: { [key: string]: ICheckboxOption[] | string[] },
                 controlName: string) => ({
                    ...acc,
                    [controlName]: FILTER_CONFIG[controlName].type === 'checkbox' ?
                        this.getCheckboxOptions(controlName) :
                        this.getSliderDefaultOptions(controlName)
                }), {}
            ) as unknown as { [controlName: string]: ICheckboxOption[] | string[][] }; // TODO NG UPGRADE cast as unknown
    }

    private getCheckboxOptions(controlName: string): ICheckboxOption[] {
        const control: AbstractControl = this.filterFormGroup.get(controlName);

        if (!control) {
            return [];
        }

        return control.value.map(
            (controlValue: string[]) => {
                return controlValue.map(
                    (option: string) => ({
                        label: this.getOptionLabel(controlName, option),
                        value: option,
                        tooltip: this.getOptionTooltip(controlName, option),
                    })
                );
            }
        );
    }

    private getSliderDefaultOptions(controlName: string): string[][] {
        const parsedData: Map<number, string[]> = this.filterService.getReducedFilterKeys(this.filterData, controlName);
        return Array.from(parsedData.values()).map(
            (filterData: string[]) => (filterData.indexOf('direct') > -1 ? ['direct', ..._pull(filterData, 'direct')] : filterData)
        );
    }

    private getOptionLabel(controlName: string, value: string): string {
        if (controlName === "beginPlusOrMinus"
            && this.filterData
            && this.filterData.beginPlusOrMinus
            && this.filterData.beginPlusOrMinus[value]) {
            const daysDifference: number = +this.filterData.beginPlusOrMinus[value].daysDifference;

            const days: string = daysDifference
                ? this.labelService.completeLabel(
                    Yii.t("wui-iqba-js", "(J%day_difference%)"),
                    {
                        day_difference: daysDifference > 0 ? `+${daysDifference}` : `${daysDifference}`
                    })
                : "";

            const date: string = this.dateMomentPipe.transform(value, this.dateFormat.momentMedium);

            return [date, days].filter((part: string) => part).join(" ");
        }

        return value;
    }

    private getOptionTooltip(controlName: string, value: string): string {
        if (!this.dictionary) {
            return undefined;
        }

        if ((controlName === "from" || controlName === "to" || controlName === "via")
            && this.dictionary.airport
            && this.dictionary.airport[value]) {
            const airport: ICodeName = this.dictionary.airport[value].airport;

            return airport && airport.name;
        }

        if ((controlName === "carrier")
            && this.dictionary.carrier
            && this.dictionary.carrier[value]) {
            const carrier: ICodeName = this.dictionary.carrier[value];

            return carrier && carrier.name;
        }

        return undefined;
    }
}


